import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  type BusinessOperationTraceV1,
  BusinessOperationTraceV1Schema,
  isBusinessOperationTraceV1,
} from "./business-operation.js";
import { PurchaseOrderNumberSchema } from "./purchase-order-number.js";
import {
  isPurchaseReceiptRecord,
  type PurchaseReceiptRecord,
  PurchaseReceiptRecordSchema,
} from "./purchase-order-receiving.js";
import {
  derivePurchasePayableReconciliationNumber,
  deriveSupplierPaymentNumber,
  isBusinessNumberSourceId,
  PurchasePayableReconciliationNumberSchema,
  SupplierPaymentNumberSchema,
} from "./purchase-payable-number.js";
import { PurchaseReceiptNumberSchema } from "./purchase-receipt-number.js";
import { isPurchaseReturnRecord, PurchaseReturnRecordSchema } from "./purchase-return.js";

const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const CanonicalTextPattern = "^(?:\\S|\\S[\\s\\S]*\\S)$";
const CanonicalTextSchema = (maximum: number) =>
  Type.String({ minLength: 1, maxLength: maximum, pattern: CanonicalTextPattern });
const BusinessDateSchema = Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" });
const TimestampSchema = Type.String({ minLength: 20, maxLength: 64 });
const AmountFenSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const SignedAmountFenSchema = Type.Integer({
  minimum: -Number.MAX_SAFE_INTEGER,
  maximum: Number.MAX_SAFE_INTEGER,
});
const VersionSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const IdempotencyKeySchema = Type.String({
  minLength: 8,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$",
});
const SupplierInvoiceNumberSchema = Type.String({
  minLength: 1,
  maxLength: 64,
  pattern: "^[^\\u0000-\\u001F\\u007F]+$",
});

export const ReconcilePurchasePayableRequestSchema = Type.Object(
  {
    reconciliationId: StableIdSchema,
    receiptId: StableIdSchema,
    businessDate: BusinessDateSchema,
    supplierInvoiceNumber: SupplierInvoiceNumberSchema,
    invoiceDate: BusinessDateSchema,
    invoiceSubtotalFen: AmountFenSchema,
    invoiceTaxFen: AmountFenSchema,
    expectedPayableBalanceVersion: VersionSchema,
    note: Type.Union([CanonicalTextSchema(200), Type.Null()]),
    operationTrace: BusinessOperationTraceV1Schema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type ReconcilePurchasePayableRequest = Static<typeof ReconcilePurchasePayableRequestSchema>;

export const PurchasePayableReconciliationRecordSchema = Type.Object(
  {
    reconciliationId: StableIdSchema,
    reconciliationNumber: PurchasePayableReconciliationNumberSchema,
    receiptId: StableIdSchema,
    receiptNumber: PurchaseReceiptNumberSchema,
    purchaseOrderId: StableIdSchema,
    purchaseOrderNumber: PurchaseOrderNumberSchema,
    supplierId: StableIdSchema,
    supplierName: CanonicalTextSchema(80),
    supplierVersion: VersionSchema,
    businessDate: BusinessDateSchema,
    supplierInvoiceNumber: SupplierInvoiceNumberSchema,
    invoiceDate: BusinessDateSchema,
    provisionalSubtotalFenBefore: AmountFenSchema,
    provisionalTaxFenBefore: AmountFenSchema,
    provisionalTotalFenBefore: AmountFenSchema,
    invoiceSubtotalFen: AmountFenSchema,
    invoiceTaxFen: AmountFenSchema,
    invoiceTotalFen: AmountFenSchema,
    purchasePriceVarianceFen: SignedAmountFenSchema,
    taxVarianceFen: SignedAmountFenSchema,
    payableAdjustmentFen: SignedAmountFenSchema,
    payableOutstandingFenBefore: AmountFenSchema,
    payableOutstandingFenAfter: AmountFenSchema,
    payableBalanceVersionBefore: VersionSchema,
    payableBalanceVersionAfter: VersionSchema,
    inventoryValueEffectFen: Type.Literal(0),
    note: Type.Union([CanonicalTextSchema(200), Type.Null()]),
    reconcilerActorId: StableIdSchema,
    operationTrace: BusinessOperationTraceV1Schema,
    reconciledAt: TimestampSchema,
    evidence: Type.Literal("STORE_ACCEPTED"),
  },
  { additionalProperties: false },
);
export type PurchasePayableReconciliationRecord = Static<
  typeof PurchasePayableReconciliationRecordSchema
>;

export const SupplierPaymentMethodSchema = Type.Union([
  Type.Literal("BANK_TRANSFER"),
  Type.Literal("CASH"),
  Type.Literal("OTHER"),
]);
export type SupplierPaymentMethod = Static<typeof SupplierPaymentMethodSchema>;

export const RecordSupplierPaymentRequestSchema = Type.Object(
  {
    paymentId: StableIdSchema,
    receiptId: StableIdSchema,
    reconciliationId: StableIdSchema,
    businessDate: BusinessDateSchema,
    amountFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    method: SupplierPaymentMethodSchema,
    externalReference: Type.Union([CanonicalTextSchema(80), Type.Null()]),
    note: Type.Union([CanonicalTextSchema(200), Type.Null()]),
    expectedPayableBalanceVersion: VersionSchema,
    operationTrace: BusinessOperationTraceV1Schema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type RecordSupplierPaymentRequest = Static<typeof RecordSupplierPaymentRequestSchema>;

export const SupplierPaymentRecordSchema = Type.Object(
  {
    paymentId: StableIdSchema,
    paymentNumber: SupplierPaymentNumberSchema,
    receiptId: StableIdSchema,
    receiptNumber: PurchaseReceiptNumberSchema,
    purchaseOrderId: StableIdSchema,
    purchaseOrderNumber: PurchaseOrderNumberSchema,
    reconciliationId: StableIdSchema,
    supplierId: StableIdSchema,
    supplierName: CanonicalTextSchema(80),
    businessDate: BusinessDateSchema,
    amountFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    method: SupplierPaymentMethodSchema,
    externalReference: Type.Union([CanonicalTextSchema(80), Type.Null()]),
    note: Type.Union([CanonicalTextSchema(200), Type.Null()]),
    payableOutstandingFenBefore: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    payableOutstandingFenAfter: AmountFenSchema,
    payableBalanceVersionBefore: VersionSchema,
    payableBalanceVersionAfter: VersionSchema,
    settlementEffect: Type.Literal("EXTERNAL_PAYMENT_RECORDED"),
    payerActorId: StableIdSchema,
    operationTrace: BusinessOperationTraceV1Schema,
    recordedAt: TimestampSchema,
    evidence: Type.Literal("STORE_ACCEPTED"),
  },
  { additionalProperties: false },
);
export type SupplierPaymentRecord = Static<typeof SupplierPaymentRecordSchema>;

export const PurchasePayableProjectionStatusSchema = Type.Union([
  Type.Literal("PENDING"),
  Type.Literal("SYNCED"),
  Type.Literal("NEEDS_REVIEW"),
]);
export const PurchasePayableReconciliationWriteResultSchema = Type.Object(
  {
    reconciliation: PurchasePayableReconciliationRecordSchema,
    cloudProjectionStatus: PurchasePayableProjectionStatusSchema,
  },
  { additionalProperties: false },
);
export type PurchasePayableReconciliationWriteResult = Static<
  typeof PurchasePayableReconciliationWriteResultSchema
>;
export const SupplierPaymentWriteResultSchema = Type.Object(
  {
    payment: SupplierPaymentRecordSchema,
    cloudProjectionStatus: PurchasePayableProjectionStatusSchema,
  },
  { additionalProperties: false },
);
export type SupplierPaymentWriteResult = Static<typeof SupplierPaymentWriteResultSchema>;

export const PurchasePayableSettlementLastActivitySchema = Type.Object(
  {
    kind: Type.Union([
      Type.Literal("RECONCILIATION"),
      Type.Literal("PAYMENT"),
      Type.Literal("CREDIT"),
      Type.Literal("REFUND"),
      Type.Literal("PAYMENT_REVERSAL"),
    ]),
    businessId: StableIdSchema,
    commandId: StableIdSchema,
    gatewaySequence: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
  },
  { additionalProperties: false },
);
export type PurchasePayableSettlementLastActivity = Static<
  typeof PurchasePayableSettlementLastActivitySchema
>;

export const PurchasePayableSettlementSummarySchema = Type.Object(
  {
    creditedTotalFen: AmountFenSchema,
    supplierRefundedTotalFen: AmountFenSchema,
    reversedPaymentTotalFen: AmountFenSchema,
    supplierRefundDueFen: AmountFenSchema,
    lastActivity: PurchasePayableSettlementLastActivitySchema,
  },
  { additionalProperties: false },
);
export type PurchasePayableSettlementSummary = Static<
  typeof PurchasePayableSettlementSummarySchema
>;

export const SupplierPaymentActivitySchema = Type.Object(
  {
    kind: Type.Union([Type.Literal("SINGLE"), Type.Literal("BATCH")]),
    paymentId: StableIdSchema,
    paymentNumber: SupplierPaymentNumberSchema,
    amountFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    method: SupplierPaymentMethodSchema,
    externalReference: Type.Union([CanonicalTextSchema(80), Type.Null()]),
    commandId: StableIdSchema,
    recordedAt: TimestampSchema,
    gatewaySequence: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
  },
  { additionalProperties: false },
);
export type SupplierPaymentActivity = Static<typeof SupplierPaymentActivitySchema>;

export const PurchasePayableWorkspaceSchema = Type.Object(
  {
    receipt: PurchaseReceiptRecordSchema,
    returns: Type.Array(PurchaseReturnRecordSchema, { maxItems: 500 }),
    reconciliation: Type.Union([PurchasePayableReconciliationRecordSchema, Type.Null()]),
    payments: Type.Array(SupplierPaymentRecordSchema, { maxItems: 500 }),
    paymentActivities: Type.Array(SupplierPaymentActivitySchema, { maxItems: 500 }),
    provisionalSubtotalFen: AmountFenSchema,
    provisionalTaxFen: AmountFenSchema,
    provisionalTotalFen: AmountFenSchema,
    payableOutstandingFen: AmountFenSchema,
    settlementSummary: Type.Union([PurchasePayableSettlementSummarySchema, Type.Null()]),
    payableBalanceVersion: VersionSchema,
    lastCommandId: StableIdSchema,
    canReconcile: Type.Boolean(),
    canPay: Type.Boolean(),
    blocker: Type.Union([CanonicalTextSchema(300), Type.Null()]),
    generatedAt: TimestampSchema,
  },
  { additionalProperties: false },
);
export type PurchasePayableWorkspace = Static<typeof PurchasePayableWorkspaceSchema>;

export const PurchasePayableReconciliationBusinessEventPayloadSchema = Type.Object(
  { reconciliation: PurchasePayableReconciliationRecordSchema },
  { additionalProperties: false },
);
export const SupplierPaymentBusinessEventPayloadSchema = Type.Object(
  { payment: SupplierPaymentRecordSchema },
  { additionalProperties: false },
);

function isBusinessDate(value: string): boolean {
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) &&
    new Date(milliseconds).toISOString() === `${value}T00:00:00.000Z`
  );
}

function isTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

function safeAdd(left: number, right: number): number | null {
  const result = left + right;
  return Number.isSafeInteger(result) ? result : null;
}

function traceMatches(
  trace: BusinessOperationTraceV1,
  receipt: PurchaseReceiptRecord,
  idempotencyKey: string,
  causationId: string,
): boolean {
  return (
    isBusinessOperationTraceV1(trace) &&
    trace.commandId === idempotencyKey &&
    trace.idempotencyKey === idempotencyKey &&
    trace.correlationId === `purchase-payable:${receipt.receiptId}` &&
    trace.causationId === causationId &&
    trace.businessNumber?.kind === "PURCHASE_ORDER" &&
    trace.businessNumber.value === receipt.purchaseOrderNumber
  );
}

export function isReconcilePurchasePayableRequest(
  value: unknown,
): value is ReconcilePurchasePayableRequest {
  if (!Value.Check(ReconcilePurchasePayableRequestSchema, value)) return false;
  const request = value as ReconcilePurchasePayableRequest;
  return (
    isBusinessDate(request.businessDate) &&
    isBusinessNumberSourceId(request.reconciliationId) &&
    isBusinessDate(request.invoiceDate) &&
    request.businessDate >= request.invoiceDate &&
    request.supplierInvoiceNumber === request.supplierInvoiceNumber.trim() &&
    safeAdd(request.invoiceSubtotalFen, request.invoiceTaxFen) !== null &&
    request.operationTrace.commandId === request.idempotencyKey &&
    request.operationTrace.idempotencyKey === request.idempotencyKey
  );
}

export function isRecordSupplierPaymentRequest(
  value: unknown,
): value is RecordSupplierPaymentRequest {
  if (!Value.Check(RecordSupplierPaymentRequestSchema, value)) return false;
  const request = value as RecordSupplierPaymentRequest;
  return (
    isBusinessDate(request.businessDate) &&
    isBusinessNumberSourceId(request.paymentId) &&
    request.operationTrace.commandId === request.idempotencyKey &&
    request.operationTrace.idempotencyKey === request.idempotencyKey &&
    (request.method === "CASH" || request.externalReference !== null)
  );
}

export function isPurchasePayableWorkspace(value: unknown): value is PurchasePayableWorkspace {
  if (!Value.Check(PurchasePayableWorkspaceSchema, value)) return false;
  const workspace = value as PurchasePayableWorkspace;
  if (
    !isTimestamp(workspace.generatedAt) ||
    !isPurchaseReceiptRecord(workspace.receipt) ||
    !workspace.returns.every(
      (item) => isPurchaseReturnRecord(item) && item.receiptId === workspace.receipt.receiptId,
    ) ||
    (workspace.reconciliation !== null &&
      (!isPurchasePayableReconciliationRecord(workspace.reconciliation) ||
        workspace.reconciliation.receiptId !== workspace.receipt.receiptId)) ||
    !workspace.payments.every(
      (item) =>
        isSupplierPaymentRecord(item) &&
        item.receiptId === workspace.receipt.receiptId &&
        item.reconciliationId === workspace.reconciliation?.reconciliationId,
    ) ||
    new Set(workspace.paymentActivities.map((item) => item.paymentId)).size !==
      workspace.paymentActivities.length ||
    !workspace.paymentActivities.every(
      (item, index) =>
        isTimestamp(item.recordedAt) &&
        (item.method === "CASH" || item.externalReference !== null) &&
        (index === 0 ||
          (workspace.paymentActivities[index - 1]?.gatewaySequence ?? Number.MAX_SAFE_INTEGER) <
            item.gatewaySequence),
    ) ||
    workspace.paymentActivities.filter((item) => item.kind === "SINGLE").length !==
      workspace.payments.length ||
    !workspace.payments.every((payment) =>
      workspace.paymentActivities.some(
        (activity) =>
          activity.kind === "SINGLE" &&
          activity.paymentId === payment.paymentId &&
          activity.paymentNumber === payment.paymentNumber &&
          activity.amountFen === payment.amountFen &&
          activity.commandId === payment.operationTrace.commandId,
      ),
    )
  ) {
    return false;
  }
  const returnedSubtotal = workspace.returns.reduce(
    (total, item) => total + item.payableSubtotalDecreaseFen,
    0,
  );
  const returnedTax = workspace.returns.reduce(
    (total, item) => total + item.payableTaxDecreaseFen,
    0,
  );
  const expectedSubtotal = workspace.receipt.subtotalFen - returnedSubtotal;
  const expectedTax = workspace.receipt.taxFen - returnedTax;
  const paymentTotal = workspace.payments.reduce((total, item) => total + item.amountFen, 0);
  const batchPaymentTotal = workspace.paymentActivities.reduce(
    (total, item) => total + (item.kind === "BATCH" ? item.amountFen : 0),
    0,
  );
  const totalPaymentRecordCount = workspace.paymentActivities.length;
  const settlementSummary = workspace.settlementSummary ?? undefined;
  const supplierCreditTotalFen = settlementSummary?.creditedTotalFen ?? 0;
  const supplierRefundTotalFen = settlementSummary?.supplierRefundedTotalFen ?? 0;
  const supplierPaymentReversalTotalFen = settlementSummary?.reversedPaymentTotalFen ?? 0;
  const expectedSignedBalance =
    (workspace.reconciliation?.invoiceTotalFen ?? expectedSubtotal + expectedTax) -
    supplierCreditTotalFen -
    paymentTotal -
    batchPaymentTotal +
    supplierPaymentReversalTotalFen +
    supplierRefundTotalFen;
  const expectedOutstanding = Math.max(expectedSignedBalance, 0);
  const expectedRefundDue = Math.max(-expectedSignedBalance, 0);
  const expectedLastCommandId =
    settlementSummary?.lastActivity.commandId ??
    workspace.payments.at(-1)?.operationTrace.commandId ??
    workspace.reconciliation?.operationTrace.commandId ??
    workspace.returns.at(-1)?.operationTrace.commandId ??
    workspace.receipt.operationTrace.commandId;
  const settlementLastActivityValid = (() => {
    const activity = settlementSummary?.lastActivity;
    if (!activity) return workspace.reconciliation === null;
    if (activity.kind === "RECONCILIATION") {
      const reconciliation = workspace.reconciliation;
      return (
        reconciliation !== null &&
        activity.businessId === reconciliation.reconciliationId &&
        activity.commandId === reconciliation.operationTrace.commandId
      );
    }
    if (activity.kind === "PAYMENT") {
      const payment = workspace.payments.find((item) => item.paymentId === activity.businessId);
      const displayedPayment = workspace.paymentActivities.find(
        (item) => item.paymentId === activity.businessId,
      );
      return (
        payment?.operationTrace.commandId === activity.commandId ||
        displayedPayment?.commandId === activity.commandId
      );
    }
    if (activity.kind === "CREDIT") return supplierCreditTotalFen > 0;
    if (activity.kind === "REFUND") return supplierRefundTotalFen > 0;
    return supplierPaymentReversalTotalFen > 0;
  })();
  return (
    Number.isSafeInteger(returnedSubtotal) &&
    Number.isSafeInteger(returnedTax) &&
    expectedSubtotal >= 0 &&
    expectedTax >= 0 &&
    workspace.provisionalSubtotalFen === expectedSubtotal &&
    workspace.provisionalTaxFen === expectedTax &&
    workspace.provisionalTotalFen === expectedSubtotal + expectedTax &&
    Number.isSafeInteger(paymentTotal) &&
    Number.isSafeInteger(batchPaymentTotal) &&
    totalPaymentRecordCount <= 500 &&
    Number.isSafeInteger(expectedSignedBalance) &&
    supplierCreditTotalFen <=
      (workspace.reconciliation?.invoiceTotalFen ?? expectedSubtotal + expectedTax) &&
    (settlementSummary === undefined) === (workspace.reconciliation === null) &&
    settlementLastActivityValid &&
    (workspace.payableOutstandingFen === 0 || expectedRefundDue === 0) &&
    workspace.payableOutstandingFen === expectedOutstanding &&
    (settlementSummary?.supplierRefundDueFen ?? 0) === expectedRefundDue &&
    workspace.lastCommandId === expectedLastCommandId &&
    workspace.canReconcile ===
      (workspace.reconciliation === null &&
        workspace.payments.length === 0 &&
        workspace.paymentActivities.length === 0 &&
        workspace.provisionalTotalFen > 0) &&
    workspace.canPay ===
      (workspace.reconciliation !== null &&
        workspace.payableOutstandingFen > 0 &&
        expectedRefundDue === 0 &&
        totalPaymentRecordCount < 500)
  );
}

export function isReconcilePurchasePayableAgainstWorkspace(
  requestValue: unknown,
  workspaceValue: unknown,
): requestValue is ReconcilePurchasePayableRequest {
  if (
    !isReconcilePurchasePayableRequest(requestValue) ||
    !isPurchasePayableWorkspace(workspaceValue)
  ) {
    return false;
  }
  const request = requestValue as ReconcilePurchasePayableRequest;
  const workspace = workspaceValue as PurchasePayableWorkspace;
  return (
    workspace.canReconcile &&
    request.receiptId === workspace.receipt.receiptId &&
    request.businessDate >= workspace.receipt.businessDate &&
    request.expectedPayableBalanceVersion === workspace.payableBalanceVersion &&
    traceMatches(
      request.operationTrace,
      workspace.receipt,
      request.idempotencyKey,
      workspace.lastCommandId,
    )
  );
}

export function isRecordSupplierPaymentAgainstWorkspace(
  requestValue: unknown,
  workspaceValue: unknown,
): requestValue is RecordSupplierPaymentRequest {
  if (
    !isRecordSupplierPaymentRequest(requestValue) ||
    !isPurchasePayableWorkspace(workspaceValue)
  ) {
    return false;
  }
  const request = requestValue as RecordSupplierPaymentRequest;
  const workspace = workspaceValue as PurchasePayableWorkspace;
  return (
    workspace.canPay &&
    workspace.reconciliation !== null &&
    request.receiptId === workspace.receipt.receiptId &&
    request.reconciliationId === workspace.reconciliation.reconciliationId &&
    request.businessDate >= workspace.reconciliation.businessDate &&
    request.amountFen <= workspace.payableOutstandingFen &&
    request.expectedPayableBalanceVersion === workspace.payableBalanceVersion &&
    traceMatches(
      request.operationTrace,
      workspace.receipt,
      request.idempotencyKey,
      workspace.lastCommandId,
    )
  );
}

export function isPurchasePayableReconciliationRecord(
  value: unknown,
): value is PurchasePayableReconciliationRecord {
  if (!Value.Check(PurchasePayableReconciliationRecordSchema, value)) return false;
  const record = value as PurchasePayableReconciliationRecord;
  const invoiceTotal = safeAdd(record.invoiceSubtotalFen, record.invoiceTaxFen);
  return (
    isBusinessDate(record.businessDate) &&
    isBusinessNumberSourceId(record.reconciliationId) &&
    isBusinessDate(record.invoiceDate) &&
    record.businessDate >= record.invoiceDate &&
    invoiceTotal === record.invoiceTotalFen &&
    record.provisionalSubtotalFenBefore + record.provisionalTaxFenBefore ===
      record.provisionalTotalFenBefore &&
    record.purchasePriceVarianceFen ===
      record.invoiceSubtotalFen - record.provisionalSubtotalFenBefore &&
    record.taxVarianceFen === record.invoiceTaxFen - record.provisionalTaxFenBefore &&
    record.payableAdjustmentFen === record.invoiceTotalFen - record.provisionalTotalFenBefore &&
    record.payableOutstandingFenAfter ===
      record.payableOutstandingFenBefore + record.payableAdjustmentFen &&
    record.payableOutstandingFenAfter >= 0 &&
    record.payableBalanceVersionAfter === record.payableBalanceVersionBefore + 1 &&
    record.reconciliationNumber ===
      derivePurchasePayableReconciliationNumber(record.businessDate, record.reconciliationId) &&
    record.operationTrace.commandId === record.operationTrace.idempotencyKey &&
    record.operationTrace.correlationId === `purchase-payable:${record.receiptId}` &&
    record.operationTrace.causationId !== null &&
    record.operationTrace.businessNumber?.kind === "PURCHASE_ORDER" &&
    record.operationTrace.businessNumber.value === record.purchaseOrderNumber &&
    isTimestamp(record.reconciledAt)
  );
}

export function isSupplierPaymentRecord(value: unknown): value is SupplierPaymentRecord {
  if (!Value.Check(SupplierPaymentRecordSchema, value)) return false;
  const record = value as SupplierPaymentRecord;
  return (
    isBusinessDate(record.businessDate) &&
    isBusinessNumberSourceId(record.paymentId) &&
    record.payableOutstandingFenAfter === record.payableOutstandingFenBefore - record.amountFen &&
    record.payableBalanceVersionAfter === record.payableBalanceVersionBefore + 1 &&
    record.paymentNumber === deriveSupplierPaymentNumber(record.businessDate, record.paymentId) &&
    (record.method === "CASH" || record.externalReference !== null) &&
    record.operationTrace.commandId === record.operationTrace.idempotencyKey &&
    record.operationTrace.correlationId === `purchase-payable:${record.receiptId}` &&
    record.operationTrace.causationId !== null &&
    record.operationTrace.businessNumber?.kind === "PURCHASE_ORDER" &&
    record.operationTrace.businessNumber.value === record.purchaseOrderNumber &&
    isTimestamp(record.recordedAt)
  );
}
